import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Split Excel Sheets V4
 *
 * Same semantics as `split_excel_sheets` but uses ExcelJS streaming under the
 * hood so the full worksheet XML is never materialised as a single JS string —
 * safe for workbooks whose uncompressed sheet data exceeds V8's ~512 MB
 * string-length limit (e.g. AS400 exports).
 *
 * Single-sheet passthrough: when exactly one worksheet survives the filter,
 * the original Document is returned unchanged — no re-upload, no format
 * conversion. Downstream steps such as `csv_transform_v4` already handle
 * XLSX natively.
 *
 * Multi-sheet: one child Document is uploaded per matching sheet. The parent
 * is tagged as MULTI_DOC_PARENT.
 */
export const splitExcelSheetsV4Step = defineStepConfig({
  type: "split_excel_sheets_v4",
  name: "Split Excel Sheets V4",
  description:
    "Split an Excel file into one document per worksheet (ExcelJS streaming, safe for large files)",
  category: "Transformation",

  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The Excel document to split into sheets",
    ),
  }),

  outputSchema: z.object({
    document: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .describe("Child document per sheet (or the original when single sheet)"),
    sheetName: z.array(z.string()).describe("Worksheet name per item"),
    sheetIndex: z
      .array(z.number())
      .describe("0-based worksheet index per item"),
  }),

  configSchema: z.object({
    sheetFilter: z
      .string()
      .optional()
      .describe(
        "Optional regex pattern to filter sheet names (e.g. '^Data' to only include sheets starting with 'Data')",
      ),
    skipEmptySheets: z
      .boolean()
      .optional()
      .default(true)
      .describe("Skip worksheets that have no data rows"),
    outputFormat: z
      .enum(["csv", "xlsx"])
      .optional()
      .default("csv")
      .describe(
        "Format for child documents when splitting multiple sheets: CSV or XLSX. Ignored for single-sheet passthrough.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
